import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { getElectricityReadings, recordElectricityReading, updateElectricityReading, deleteElectricityReading, getRooms, exportElectricityReport, getProperties } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { toast } from 'sonner';
import { Zap, Plus, Calculator, Pencil, Trash2, Save, X, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DEFAULT_RATE_PER_UNIT = 12; // Default Rs. 12 per unit (used when no property rate configured)

export const Electricity = () => {
  const { canAdd, canEdit, canDelete } = useAuth();
  const [readings, setReadings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [editingReading, setEditingReading] = useState(null);
  
  const [form, setForm] = useState({
    room_id: '',
    current_reading: 0,
    month_year: selectedMonth,
    start_from_zero: false
  });
  
  const [editForm, setEditForm] = useState({
    previous_reading: 0,
    current_reading: 0,
    reading_date: ''
  });
  
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedRoom, selectedMonth]);

  const fetchData = async () => {
    try {
      const params = {};
      if (selectedRoom !== 'all') params.room_id = selectedRoom;
      if (selectedMonth) params.month = selectedMonth;
      
      const [readingsRes, roomsRes, propsRes] = await Promise.all([
        getElectricityReadings(params),
        getRooms(),
        getProperties()
      ]);
      setReadings(readingsRes.data);
      setProperties(propsRes.data || []);
      // Sort rooms by combined_number (alphanumeric sorting)
      const sortedRooms = [...roomsRes.data].sort((a, b) => {
        const aNum = a.combined_number || '';
        const bNum = b.combined_number || '';
        return aNum.localeCompare(bNum, undefined, { numeric: true, sensitivity: 'base' });
      });
      setRooms(sortedRooms);
    } catch (err) {
      toast.error('Failed to load electricity data');
    } finally {
      setLoading(false);
    }
  };

  // Resolve applicable ₹/unit rate for a property + reading month (mirrors backend logic)
  const resolveRate = (propertyId, monthYear) => {
    const prop = properties.find(p => p.id === propertyId);
    if (!prop) return DEFAULT_RATE_PER_UNIT;
    const baseRate = (prop.electricity_rate_per_unit != null && prop.electricity_rate_per_unit > 0)
      ? prop.electricity_rate_per_unit : DEFAULT_RATE_PER_UNIT;
    const hist = prop.electricity_rate_history || [];
    const applicable = hist.filter(h => (h.effective_from_month || '') <= (monthYear || ''));
    if (applicable.length) {
      applicable.sort((a, b) => ((a.effective_from_month || '') > (b.effective_from_month || '') ? 1 : -1));
      return applicable[applicable.length - 1].rate;
    }
    if (hist.length) {
      // month is before the earliest recorded rate → use the baseline (earliest) rate
      const earliest = [...hist].sort((a, b) => ((a.effective_from_month || '') > (b.effective_from_month || '') ? 1 : -1))[0];
      return earliest.rate;
    }
    return baseRate;
  };

  // Rate shown on the page header / summary card — resolved for the MONTH being viewed
  // (so a past month like June shows its original ₹12, a month on the new rate shows the new rate)
  const headerPropertyId = selectedRoom !== 'all'
    ? (rooms.find(r => r.id === selectedRoom) || {}).property_id
    : properties[0]?.id;
  const headerRate = resolveRate(headerPropertyId, selectedMonth);

  const handleRoomSelect = (roomId) => {
    setForm({ ...form, room_id: roomId });
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      const previous = form.start_from_zero ? 0 : (room.electricity_current_reading || 0);
      const rate = resolveRate(room.property_id, selectedMonth);
      setPreview({
        previous: previous,
        current: form.current_reading,
        units: 0,
        charges: 0,
        rate,
        totalBeds: room.sharing_type,
        occupiedBeds: room.beds?.filter(b => b.status === 'occupied' || b.status === 'notice').length || 0
      });
    }
  };

  const handleReadingChange = (value) => {
    const current = parseFloat(value) || 0;
    setForm({ ...form, current_reading: current });
    
    updatePreview(current, form.start_from_zero);
  };

  const handleStartFromZeroChange = (checked) => {
    setForm({ ...form, start_from_zero: checked });
    updatePreview(form.current_reading, checked);
  };

  const updatePreview = (current, startFromZero) => {
    if (form.room_id) {
      const room = rooms.find(r => r.id === form.room_id);
      if (room) {
        const previous = startFromZero ? 0 : (room.electricity_current_reading || 0);
        const units = Math.max(0, current - previous);
        const rate = resolveRate(room.property_id, selectedMonth);
        const charges = units * rate;
        const totalBeds = room.sharing_type;
        const occupiedBeds = room.beds?.filter(b => b.status === 'occupied' || b.status === 'notice').length || 0;
        // Per Bed Charges = Total Charges / Total Number of Beds
        const perBed = totalBeds > 0 ? charges / totalBeds : charges;
        setPreview({ 
          previous, 
          current, 
          units, 
          charges,
          rate,
          totalBeds,
          occupiedBeds,
          perBed
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await recordElectricityReading({
        ...form,
        month_year: selectedMonth
      });
      toast.success(`Reading recorded. Total charges: ₹${res.data.total_charges}`);
      setDialogOpen(false);
      setForm({ room_id: '', current_reading: 0, month_year: selectedMonth, start_from_zero: false });
      setPreview(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record reading');
    }
  };

  const handleEditClick = (reading) => {
    setEditingReading(reading);
    setEditForm({
      previous_reading: reading.previous_reading || 0,
      current_reading: reading.current_reading,
      reading_date: reading.reading_date
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateElectricityReading(editingReading.id, editForm);
      toast.success('Reading updated');
      setEditDialogOpen(false);
      setEditingReading(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update reading');
    }
  };

  const handleDelete = async (readingId) => {
    if (!window.confirm('Are you sure you want to delete this reading?')) return;
    try {
      await deleteElectricityReading(readingId);
      toast.success('Reading deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete reading');
    }
  };

  const propRooms = selectedProperty === 'all' ? rooms : rooms.filter(r => r.property_id === selectedProperty);
  const roomIdsInProp = new Set(propRooms.map(r => r.id));
  const scopedReadings = selectedProperty === 'all' ? readings : readings.filter(r => roomIdsInProp.has(r.room_id));

  const totalCharges = scopedReadings.reduce((sum, r) => sum + r.total_charges, 0);
  const totalUnits = scopedReadings.reduce((sum, r) => sum + r.units_consumed, 0);
  const totalNetReceivable = scopedReadings.reduce((sum, r) => {
    return sum + r.bed_wise_charges?.reduce((s, b) => s + (b.net_receivable || 0), 0) || 0;
  }, 0);

  const handleExportExcel = async () => {
    if (!selectedMonth) {
      toast.error('Please select a month to export');
      return;
    }
    try {
      const response = await exportElectricityReport(selectedMonth);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Electricity_Report_${selectedMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Electricity report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="electricity-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Electricity Billing</h1>
          <p className="text-slate-500 mt-1">Track meter readings and calculate bills (₹{headerRate}/unit)</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleExportExcel} 
            className="!bg-emerald-600 hover:!bg-emerald-700 text-white"
            data-testid="export-electricity-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            {canAdd('electricity') && (
              <DialogTrigger asChild>
                <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-reading-btn">
                  <Plus className="h-4 w-4 mr-2" />
                Record Reading
              </Button>
            </DialogTrigger>
            )}
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Record Meter Reading</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Room</Label>
                <Select value={form.room_id} onValueChange={handleRoomSelect}>
                  <SelectTrigger data-testid="reading-room-select">
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.combined_number} ({r.property_name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Previous Reading - Auto from System with Reset Option */}
              {form.room_id && (
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-slate-600">Previous Reading (from system)</Label>
                      <p className="text-2xl font-mono font-bold text-slate-800">
                        {form.start_from_zero ? '0' : (rooms.find(r => r.id === form.room_id)?.electricity_current_reading || 0)}
                      </p>
                    </div>
                    <Button 
                      type="button"
                      variant={form.start_from_zero ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleStartFromZeroChange(!form.start_from_zero)}
                      className={form.start_from_zero ? "bg-blue-600" : ""}
                    >
                      {form.start_from_zero ? "✓ Reset to Zero" : "Reset to Zero"}
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Current Reading</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.current_reading}
                  onChange={(e) => handleReadingChange(e.target.value)}
                  required
                  placeholder="Enter current meter reading"
                  className="text-lg font-mono"
                  data-testid="current-reading-input"
                />
              </div>

              {/* Preview Calculation - Like Excel Report */}
              {preview && form.room_id && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 text-amber-700 font-semibold">
                    <Calculator className="h-4 w-4" />
                    Calculation Preview
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-slate-600">Total Beds:</div>
                    <div className="font-mono text-right">{preview.totalBeds} Sharing</div>
                    <div className="text-slate-600">Previous Reading:</div>
                    <div className="font-mono text-right">{preview.previous} {form.start_from_zero && <span className="text-blue-600">(from zero)</span>}</div>
                    <div className="text-slate-600">Current Reading:</div>
                    <div className="font-mono text-right">{form.current_reading}</div>
                    <div className="text-slate-600">Difference (Units):</div>
                    <div className="font-mono text-right">{Math.max(0, form.current_reading - preview.previous)}</div>
                    <div className="text-slate-600">Rate per Unit:</div>
                    <div className="font-mono text-right">₹{preview.rate ?? DEFAULT_RATE_PER_UNIT}</div>
                    <div className="text-slate-900 font-semibold border-t pt-2">Total Charges:</div>
                    <div className="font-mono text-right font-bold text-amber-700 border-t pt-2">
                      ₹{(Math.max(0, form.current_reading - preview.previous) * (preview.rate ?? DEFAULT_RATE_PER_UNIT)).toLocaleString()}
                    </div>
                    <div className="text-slate-600">Total Beds:</div>
                    <div className="font-mono text-right">{preview.totalBeds}</div>
                    <div className="text-slate-600">Occupied Beds:</div>
                    <div className="font-mono text-right">{preview.occupiedBeds}</div>
                    <div className="text-emerald-700 font-semibold">Per Bed Charge (Total/Beds):</div>
                    <div className="font-mono text-right font-bold text-emerald-700">
                      ₹{preview.totalBeds > 0 ? ((Math.max(0, form.current_reading - preview.previous) * (preview.rate ?? DEFAULT_RATE_PER_UNIT)) / preview.totalBeds).toFixed(2) : 0}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-blue-800 hover:bg-blue-900" data-testid="save-reading-btn">
                  Record Reading
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        {properties.length > 1 && (
          <Select value={selectedProperty} onValueChange={(v) => { setSelectedProperty(v); setSelectedRoom('all'); }}>
            <SelectTrigger className="w-[190px]" data-testid="electricity-property-select">
              <SelectValue placeholder="All Properties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        </div>
      </div>

      {/* Edit Reading Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Reading - {editingReading?.room_info}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Previous Reading</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editForm.previous_reading}
                onChange={(e) => setEditForm({ ...editForm, previous_reading: parseFloat(e.target.value) || 0 })}
                className="font-mono"
              />
              <p className="text-xs text-slate-500">Set to 0 if you want to reset previous reading</p>
            </div>
            <div className="space-y-2">
              <Label>Current Reading</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editForm.current_reading}
                onChange={(e) => setEditForm({ ...editForm, current_reading: parseFloat(e.target.value) || 0 })}
                required
                className="font-mono"
              />
            </div>
            {/* Preview */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                <strong>Units:</strong> {Math.max(0, (editForm.current_reading || 0) - (editForm.previous_reading || 0))} | 
                <strong> Total Charges:</strong> ₹{(Math.max(0, (editForm.current_reading || 0) - (editForm.previous_reading || 0)) * (editingReading?.rate_per_unit ?? DEFAULT_RATE_PER_UNIT)).toLocaleString()}
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <Save className="h-4 w-4 mr-1" /> Update
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={selectedRoom} onValueChange={setSelectedRoom}>
          <SelectTrigger className="w-[250px]" data-testid="room-filter">
            <SelectValue placeholder="All Rooms" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Rooms</SelectItem>
            {propRooms.map(r => (
              <SelectItem key={r.id} value={r.id}>{r.combined_number} ({r.property_name})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="w-[180px]"
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-amber-200" style={{ animationDelay: '0ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 opacity-10"></div>
          <Zap className="h-5 w-5 text-amber-600 mb-2" />
          <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Total Units</p>
          <p className="text-2xl font-bold font-mono text-amber-700 mt-0.5">{totalUnits.toLocaleString()}</p>
        </div>
        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-blue-200" style={{ animationDelay: '60ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 opacity-10"></div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Total Charges</p>
          <p className="text-2xl font-bold font-mono text-blue-700 mt-0.5">₹{totalCharges.toLocaleString()}</p>
        </div>
        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-emerald-200" style={{ animationDelay: '120ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 opacity-10"></div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Net Receivable</p>
          <p className="text-2xl font-bold font-mono text-emerald-700 mt-0.5">₹{Math.round(totalNetReceivable).toLocaleString()}</p>
        </div>
        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-slate-200" style={{ animationDelay: '180ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 opacity-10"></div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Rate per Unit</p>
          <p className="text-2xl font-bold font-mono text-slate-700 mt-0.5">₹{headerRate}</p>
        </div>
      </div>

      {/* Electricity Report Table - Excel Format */}
      <Card className="dash-animate relative overflow-hidden" data-testid="readings-table" style={{ animationDelay: '220ms' }}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-orange-600 z-10" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Electricity Report - {selectedMonth || 'All Time'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-bold">Sr No</TableHead>
                  <TableHead className="font-bold">Room</TableHead>
                  <TableHead className="font-bold">Sharing</TableHead>
                  <TableHead className="text-right font-bold">Prev Reading</TableHead>
                  <TableHead className="text-right font-bold">Curr Reading</TableHead>
                  <TableHead className="text-right font-bold">Difference</TableHead>
                  <TableHead className="text-right font-bold">Unit Rate</TableHead>
                  <TableHead className="text-right font-bold">Total Charges</TableHead>
                  <TableHead className="text-center font-bold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scopedReadings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-14">
                      <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                        <Zap className="w-8 h-8 text-amber-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-700 mb-1">No readings for {selectedMonth}</h3>
                      <p className="text-slate-500 mb-5 text-sm">Record meter readings to calculate this month's electricity bills.</p>
                      {canEdit('electricity') && (
                        <Button onClick={() => setDialogOpen(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="empty-record-reading-btn">
                          <Plus className="w-4 h-4 mr-2" /> Record Reading
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : scopedReadings.map((reading, idx) => (
                  <TableRow key={reading.id} data-testid={`reading-row-${reading.id}`} className="transition-colors hover:bg-slate-50">
                    <TableCell className="font-mono">{idx + 1}</TableCell>
                    <TableCell className="font-mono font-medium">{reading.room_info}</TableCell>
                    <TableCell>{reading.sharing_type} Sharing</TableCell>
                    <TableCell className="text-right font-mono">{reading.previous_reading}</TableCell>
                    <TableCell className="text-right font-mono">{reading.current_reading}</TableCell>
                    <TableCell className="text-right font-mono text-amber-600 font-medium">{reading.units_consumed}</TableCell>
                    <TableCell className="text-right font-mono">₹{reading.rate_per_unit}</TableCell>
                    <TableCell className="text-right font-mono font-medium text-blue-600">
                      ₹{reading.total_charges.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-center">
                      {(canEdit('electricity') || canDelete('electricity')) && (
                        <div className="flex justify-center gap-1">
                          {canEdit('electricity') && (
                            <Button variant="ghost" size="icon" onClick={() => handleEditClick(reading)} className="h-7 w-7" data-testid={`edit-reading-${reading.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {canDelete('electricity') && (
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(reading.id)} className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-reading-${reading.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Summary Row */}
                {readings.length > 0 && (
                  <TableRow className="bg-slate-100 font-bold">
                    <TableCell colSpan={5} className="text-right">TOTAL</TableCell>
                    <TableCell className="text-right font-mono text-amber-700">{totalUnits}</TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right font-mono text-blue-700">₹{totalCharges.toLocaleString()}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Bed-wise Breakdown */}
      {readings.some(r => r.bed_wise_charges?.length > 0) && (
        <Card className="dash-animate relative overflow-hidden" style={{ animationDelay: '300ms' }}>
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600 z-10" />
          <CardHeader>
            <CardTitle>Bed-wise Charge Distribution</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Room</TableHead>
                  <TableHead>Bed No</TableHead>
                  <TableHead>Guest Name</TableHead>
                  <TableHead className="text-right">Shared Charges</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {readings.flatMap(reading => 
                  (reading.bed_wise_charges || []).map((bed, idx) => (
                    <TableRow key={`${reading.id}-${idx}`} className="transition-colors hover:bg-slate-50">
                      <TableCell className="font-mono">{reading.room_info}</TableCell>
                      <TableCell>Bed {bed.bed_no}</TableCell>
                      <TableCell>{bed.guest_name || '-'}</TableCell>
                      <TableCell className="text-right font-mono font-medium text-emerald-600">₹{bed.charges?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
